import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { AlertTriangle, ChevronLeft, ChevronRight, DollarSign, Download, Loader2, Plus } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { DailySummary, Invoice, InvoiceStatus, STATUS_COLORS, STATUS_LABELS } from '../types/invoice';

interface OutstandingInvoice {
  id: string;
  patientId: string;
  patientName: string;
  invoiceNumber: string;
  invoiceDate: string;
  daysOutstanding: number;
  amountDue: number;
  subtotal: number;
}

function agingColor(days: number): string {
  if (days > 60) return 'text-red-600 font-bold';
  if (days > 30) return 'text-amber-600 font-medium';
  return 'text-green-700';
}

const STATUS_FILTERS: { value: string; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'draft', label: 'Draft' },
  { value: 'sent', label: 'Sent' },
  { value: 'paid', label: 'Paid' },
  { value: 'voided', label: 'Voided' },
];

export function BillingPage() {
  const navigate = useNavigate();
  const { get, getBlob } = useApi();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Day analysis
  const [analysisDate, setAnalysisDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [summary, setSummary] = useState<DailySummary | null>(null);

  // Outstanding / debtors
  const [outstanding, setOutstanding] = useState<OutstandingInvoice[]>([]);

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const result = await get<Invoice[]>(`/invoices${params}`);
      if (result.success && result.data) setInvoices(result.data);
      else setError(result.error || 'Failed to load');
    } catch { setError('Failed to load invoices'); }
    finally { setLoading(false); }
  }, [get, statusFilter]);

  const fetchSummary = useCallback(async () => {
    try {
      const result = await get<DailySummary>(`/invoices/daily-summary/${analysisDate}`);
      if (result.success && result.data) setSummary(result.data);
    } catch { /* non-critical */ }
  }, [get, analysisDate]);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);
  const fetchOutstanding = useCallback(async () => {
    try {
      const result = await get<OutstandingInvoice[]>('/invoices/outstanding');
      if (result.success && result.data) setOutstanding(result.data);
    } catch { /* non-critical */ }
  }, [get]);

  useEffect(() => { fetchSummary(); fetchOutstanding(); }, [fetchSummary, fetchOutstanding]);

  const shiftDate = useCallback((delta: number) => {
    const d = new Date(analysisDate);
    d.setDate(d.getDate() + delta);
    setAnalysisDate(format(d, 'yyyy-MM-dd'));
  }, [analysisDate]);

  return (
    <main className="max-w-7xl mx-auto px-4 py-6">
      {/* Day analysis bar */}
      {summary && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Day Analysis</h2>
            <div className="flex items-center gap-2">
              <button onClick={() => shiftDate(-1)} className="p-1 rounded hover:bg-gray-100"><ChevronLeft className="h-4 w-4" /></button>
              <input type="date" value={analysisDate} onChange={e => setAnalysisDate(e.target.value)}
                className="text-sm px-2 py-1 border border-gray-300 rounded" />
              <button onClick={() => shiftDate(1)} className="p-1 rounded hover:bg-gray-100"><ChevronRight className="h-4 w-4" /></button>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-gray-500">Total Billed</p>
              <p className="text-lg font-bold text-gray-900">R {summary.totalBilled.toFixed(2)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Total Collected</p>
              <p className="text-lg font-bold text-green-700">R {summary.totalCollected.toFixed(2)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Outstanding</p>
              <p className="text-lg font-bold text-red-600">R {summary.outstanding.toFixed(2)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Invoices / Payments</p>
              <p className="text-lg font-bold text-gray-900">{summary.invoiceCount} / {summary.paymentCount}</p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold text-gray-900">Invoices</h1>
        <div className="flex items-center gap-3">
          <div className="flex border border-gray-300 rounded-md overflow-hidden">
            {STATUS_FILTERS.map(f => (
              <button key={f.value} onClick={() => setStatusFilter(f.value)}
                className={`px-3 py-1.5 text-xs font-medium ${statusFilter === f.value ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>
                {f.label}
              </button>
            ))}
          </div>
          <button
            onClick={async () => {
              const params = new URLSearchParams();
              if (statusFilter) params.set('status', statusFilter);
              const blob = await getBlob(`/invoices/export/csv?${params}`);
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a'); a.href = url; a.download = 'invoices_export.csv'; a.click();
              URL.revokeObjectURL(url);
            }}
            className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-1.5">
            <Download className="h-4 w-4" /> Export CSV
          </button>
          <button onClick={() => navigate('/billing/new')}
            className="px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 flex items-center gap-1.5">
            <Plus className="h-4 w-4" /> New Invoice
          </button>
        </div>
      </div>

      {error && <div className="mb-4 p-3 rounded bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>
      ) : invoices.length === 0 ? (
        <div className="text-center py-12 text-gray-500">No invoices found.</div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice #</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Patient</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Balance</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {invoices.map(inv => (
                <tr key={inv.id} onClick={() => navigate(`/billing/${inv.id}`)}
                  className="hover:bg-gray-50 cursor-pointer transition-colors">
                  <td className="px-6 py-4 text-sm font-mono text-gray-900">{inv.invoiceNumber}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{inv.patientName}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{inv.createdAt ? inv.createdAt.slice(0, 10) : ''}</td>
                  <td className="px-6 py-4 text-sm text-gray-900 text-right">R {inv.subtotal.toFixed(2)}</td>
                  <td className="px-6 py-4 text-sm text-right font-medium" style={{ color: inv.balance > 0 ? '#dc2626' : '#16a34a' }}>
                    R {inv.balance.toFixed(2)}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${STATUS_COLORS[inv.status as InvoiceStatus]}`}>
                      {STATUS_LABELS[inv.status as InvoiceStatus] || inv.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Outstanding Balances / Debtors Age Analysis */}
      {outstanding.length > 0 && (
        <div className="mt-8">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            <h2 className="text-lg font-bold text-gray-900">Outstanding Balances</h2>
            <span className="text-sm text-gray-500">({outstanding.length} invoices)</span>
          </div>
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Patient</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice #</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice Date</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Days Outstanding</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount Due</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {outstanding.map(o => (
                  <tr key={o.id} onClick={() => navigate(`/billing/${o.id}`)}
                    className="hover:bg-gray-50 cursor-pointer transition-colors">
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <button onClick={e => { e.stopPropagation(); navigate(`/patients/${o.patientId}`); }}
                        className="text-blue-600 hover:underline">{o.patientName}</button>
                    </td>
                    <td className="px-6 py-4 text-sm font-mono text-gray-900">{o.invoiceNumber}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{o.invoiceDate}</td>
                    <td className={`px-6 py-4 text-sm text-right ${agingColor(o.daysOutstanding)}`}>
                      {o.daysOutstanding} days
                    </td>
                    <td className="px-6 py-4 text-sm text-right font-medium text-red-600">
                      R {o.amountDue.toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </main>
  );
}
